<script lang="ts" module>
  import { shapeBBox, markerPosition } from '../../domain/geometry';

  export function bboxForSelection(shape: import('../../domain/types').Shape) {
    return shapeBBox(shape);
  }
  /**
   * Позиция маркера (номера/!) над фигурой.
   * Для стрелки — над серединой линии (с лёгким смещением вверх),
   * для остальных — по центру верхней грани bbox.
   */
  export function markerBBox(shape: import('../../domain/types').Shape) {
    if (shape.type === 'arrow') {
      // середина отрезка (для змейки — середина bbox пути)
      const cx = (shape.x + shape.x2) / 2;
      const cy = (shape.y + shape.y2) / 2;
      return { cx, cy: cy - 28 };
    }
    const bb = shapeBBox(shape);
    const p = markerPosition(bb);
    return { cx: p.x, cy: p.y - 24 };
  }
</script>

<script lang="ts">
  /**
   * Рендер одного shape в SVG. Чисто отображение; геометрия — в domain/geometry.
   */
  import {
    arrowGeometry,
    catmullRomPath,
    numberArrowEnd,
    numberArrowStart,
    polylinePath,
  } from '../../domain/geometry';
  import { editor } from '../../store/editor.svelte';
  import type { Shape } from '../../domain/types';

  interface Props {
    shape: Shape;
    selected?: boolean;
  }
  let { shape, selected = false }: Props = $props();

  const opacity = $derived(shape.opacity);
</script>

{#if shape.type === 'arrow'}
  {@const geom = arrowGeometry({ x: shape.x, y: shape.y }, { x: shape.x2, y: shape.y2 }, shape.headStyle, shape.headSize, {
    strokeWidth: shape.strokeWidth,
    dash: shape.dash,
    path: shape.path,
  })}
  <!-- Тело стрелки: острая с обоих концов, толщина = strokeWidth (Размер). -->
  <path d={geom.fillPath} fill={shape.strokeColor} stroke="none" {opacity} />
  <!-- Пунктир/точки — отдельной осью поверх тела, чтобы не искажать заливку. -->
  {#if shape.dash !== 'solid'}
    <path
      d={geom.axisPath}
      stroke="#ffffff"
      stroke-width={shape.dash === 'dotted' ? Math.max(10, shape.strokeWidth) : Math.max(4, shape.strokeWidth * 0.5)}
      stroke-linecap="round"
      stroke-dasharray={geom.dashArray}
      fill="none"
      {opacity}
    />
  {/if}
  {@render renderMarker(shape)}
{:else if shape.type === 'rect'}
  {#if shape.fillEnabled && shape.fillMode !== 'blur' && shape.fillMode !== 'pixelize'}
    <rect
      x={shape.x}
      y={shape.y}
      width={shape.w}
      height={shape.h}
      rx={shape.cornerRadius}
      ry={shape.cornerRadius}
      stroke="none"
      fill={shape.fillColor}
      {opacity}
    />
  {/if}
  {#if shape.strokeEnabled}
    <rect
      x={shape.x - shape.strokeWidth / 2}
      y={shape.y - shape.strokeWidth / 2}
      width={shape.w + shape.strokeWidth}
      height={shape.h + shape.strokeWidth}
      rx={shape.cornerRadius + shape.strokeWidth / 2}
      ry={shape.cornerRadius + shape.strokeWidth / 2}
      stroke={shape.strokeColor}
      stroke-width={shape.strokeWidth}
      fill="none"
      {opacity}
    />
  {/if}
  {@render renderMarker(shape)}

{:else if shape.type === 'pencil'}
  {@const d = shape.points.length > 2 ? catmullRomPath(shape.points, shape.closed) : polylinePath(shape.points, shape.closed)}
  <path
    d={d}
    stroke={shape.strokeEnabled ? shape.strokeColor : 'none'}
    stroke-width={shape.strokeWidth}
    stroke-linecap="round"
    stroke-linejoin="round"
    fill={shape.closed && shape.fillEnabled ? shape.fillColor : 'none'}
    {opacity}
  />
{:else if shape.type === 'number'}
  {@const end = numberArrowEnd(shape)}
  {@const start = numberArrowStart(shape)}
  {#if shape.hasArrow}
    {@const geom = arrowGeometry(start, end, shape.headStyle, shape.headSize, {
      strokeWidth: shape.strokeWidth,
    })}
    <path d={geom.fillPath} fill={shape.strokeColor} stroke="none" {opacity} />
  {/if}
  {@const numFontSize = shape.radius * 1.1}
  <circle
    cx={shape.x}
    cy={shape.y}
    r={shape.strokeEnabled ? shape.radius + shape.strokeWidth / 2 : shape.radius}
    fill={shape.fillEnabled ? shape.fillColor : shape.strokeColor}
    stroke={shape.strokeEnabled ? shape.strokeColor : 'none'}
    stroke-width={shape.strokeEnabled ? shape.strokeWidth : 0}
    {opacity}
  />
  <text
    x={shape.x}
    y={shape.y + numFontSize * 0.35}
    text-anchor="middle"
    fill={shape.textColor}
    font-size={numFontSize}
    font-weight="bold"
    font-family="Inter, system-ui, sans-serif"
    {opacity}
  >{shape.value}</text>
{:else if shape.type === 'text'}
  {#if editor.editingTextId !== shape.id}
    {@const lines = shape.text.split('\n')}
    {@const lh = shape.fontSize * 1.25}
    {#if shape.hasBackground}
      {@const maxLen = Math.max(1, ...lines.map((l) => l.length))}
      {@const w = maxLen * shape.fontSize * 0.6 + 16}
      {@const h = lines.length * lh + 12}
      <rect x={shape.x} y={shape.y} width={w} height={h} rx={6} fill={shape.fillColor} {opacity} />
    {/if}
    {#each lines as line, i}
      {@const decorations = [shape.underline ? 'underline' : null, shape.strikethrough ? 'line-through' : null].filter(Boolean).join(' ') || 'none'}
      <text
        x={shape.x + 8}
        y={shape.y + shape.fontSize + i * lh}
        fill={shape.textColor}
        font-size={shape.fontSize}
        font-family={shape.fontFamily}
        font-weight={shape.bold ? 'bold' : 'normal'}
        font-style={shape.italic ? 'italic' : 'normal'}
        text-decoration={decorations}
        text-anchor="start"
        {opacity}
      >{line}</text>
    {/each}
  {/if}
{:else if shape.type === 'blur'}
  <!-- Контур маски поверх обработанного тайла (сам тайл рисует MaskLayer) -->
  <rect
    x={shape.x}
    y={shape.y}
    width={shape.w}
    height={shape.h}
    fill="none"
    stroke={shape.strokeEnabled ? shape.strokeColor : '#ff453a'}
    stroke-width={shape.strokeWidth}
    stroke-dasharray="6 4"
    {opacity}
  />
{/if}

{#if selected}
  {@const bb = bboxForSelection(shape)}
  {@const offset = shape.type === 'rect' ? 0 : 4}
  <rect
    x={bb.x - offset}
    y={bb.y - offset}
    width={bb.w + 2 * offset}
    height={bb.h + 2 * offset}
    fill="none"
    stroke="#3b82f6"
    stroke-width="2"
    stroke-dasharray="4 4"
    pointer-events="none"
  />
  {#if shape.type === 'rect' || shape.type === 'number'}
    <circle
      cx={bb.x + bb.w + offset}
      cy={bb.y + bb.h + offset}
      r="5"
      fill="#ffffff"
      stroke="#3b82f6"
      stroke-width="2"
      pointer-events="none"
    />
  {/if}
{/if}

{#snippet renderMarker(s: Shape)}
  {#if (s.type === 'rect' || s.type === 'arrow') && s.marker}
    {@const bb = markerBBox(s)}
    <circle cx={bb.cx} cy={bb.cy} r={16} fill={s.strokeColor} opacity={s.opacity} />
    <text x={bb.cx} y={bb.cy + 5} text-anchor="middle" fill="white" font-size="16" font-weight="bold" font-family="Inter, system-ui, sans-serif" opacity={s.opacity}>{s.marker.kind === 'number' ? s.marker.value : '!'}</text>
  {/if}
{/snippet}
